import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook pour scroller automatiquement vers le haut
 */
export const useScrollToTop = (enabled: boolean = true) => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (enabled) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
    }
  }, [pathname, enabled]);
};

/**
 * Hook qui retourne une fonction pour scroller vers le haut manuellement
 */
export const useScrollToTopFunction = () => {
  const scrollToTop = useCallback((smooth: boolean = false) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: smooth ? 'smooth' : 'instant'
    });
  }, []);

  return scrollToTop;
};

/**
 * Hook pour scroller vers le haut au montage du composant
 * Force le scroll immédiatement et après un court délai pour garantir l'effet
 */
export const useScrollToTopOnMount = (smooth: boolean = false) => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Force le scroll immédiatement
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
    
    // Scroll avec l'API moderne
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
    
    // Scroll après le premier frame
    const rafId = requestAnimationFrame(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    });
    
    // Scroll après un court délai pour le contenu asynchrone
    const timeoutId = setTimeout(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'instant'
      });
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, [pathname]); // Dépend du pathname pour se déclencher à chaque changement de page
};
